import { SEVERITY_ORDER, type SecurityEventSummary } from '@bartizo/shared';
import { Link } from 'react-router';
import { formatInstant, formatRelative } from '../lib/time.js';
import type { Column } from '../ui/DataTable.js';
import { AddressLink } from './AddressStatus.js';
import { Flag } from './Flag.js';
import { SeverityBadge } from './StatusBadge.js';

const SEVERITY_RANK = Object.fromEntries(SEVERITY_ORDER.map((each, index) => [each, index]));

/**
 * The list's columns: what, how bad, where, when, and whether anyone has it.
 * A server's own page leaves out the server.
 */
export function eventColumns(
  timezone: string,
  { withServer = true }: { withServer?: boolean } = {},
): Column<SecurityEventSummary>[] {
  const columns: Column<SecurityEventSummary>[] = [
    {
      key: 'severity',
      header: 'Severity',
      sortValue: (event) => SEVERITY_RANK[event.severity] ?? 0,
      cell: (event) => <SeverityBadge severity={event.severity} />,
    },
    {
      key: 'what',
      header: 'What happened',
      rowHeader: true,
      cell: (event) => (
        <>
          <span className={event.acknowledged ? 'muted' : undefined}>{event.message}</span>
          <span className="cell-sub">
            {event.category}
            {event.sourceIp !== null && (
              <>
                {' · from '}
                <AddressLink ip={event.sourceIp} />
                {event.country !== null && (
                  <>
                    {' '}
                    <Flag code={event.country} />
                  </>
                )}
              </>
            )}
            {event.mitre !== null && event.mitre.techniques.length > 0 && (
              <>
                {' · ATT&CK '}
                {event.mitre.techniques.map((technique, i) => (
                  <span key={technique.id}>
                    {i > 0 && ', '}
                    <Link
                      to={`/events?technique=${encodeURIComponent(technique.id)}`}
                      title={[technique.name, ...(event.mitre?.tactics ?? [])]
                        .filter((each) => each !== null)
                        .join(' · ')}
                    >
                      {technique.id}
                    </Link>
                  </span>
                ))}
              </>
            )}
            {event.hasNote && ' · has a note'}
          </span>
        </>
      ),
    },
    {
      key: 'server',
      header: 'Server',
      sortValue: (event) => event.serverName,
      cell: (event) => (
        <Link to={`/servers/${encodeURIComponent(event.serverId)}`}>{event.serverName}</Link>
      ),
    },
    {
      key: 'when',
      header: 'When',
      sortValue: (event) => Date.parse(event.occurredAt),
      cell: (event) => (
        <span title={formatInstant(event.occurredAt, { timezone })}>
          {formatRelative(event.occurredAt)}
        </span>
      ),
    },
    {
      key: 'state',
      header: 'State',
      wideOnly: true,
      cell: (event) => (
        <>
          {event.acknowledged ? (
            <span className="badge badge-muted">Acknowledged</span>
          ) : (
            <span className="badge badge-accent">Open</span>
          )}
          {event.assignee !== null && <span className="cell-sub">{event.assignee.email}</span>}
        </>
      ),
    },
  ];
  return withServer ? columns : columns.filter((column) => column.key !== 'server');
}
